import { styled } from '@mui/system';

import { Box, Typography, Paper } from '@mui/material';

import { colors, fontFamilies } from '../../../../../constants/theme';
import { CTAButton } from '../../../../../components/Header/styles';

export const StorySectionWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
  borderRadius: '40px',
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(3, 2),
  },
}));

export const StoryPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '1280px',
  height: '986px',
  borderRadius: '40px',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  [theme.breakpoints.down('xl')]: {
    width: '100%',
    maxWidth: '1280px',
    height: 'auto',
    minHeight: '600px',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    minHeight: 'auto',
  },
}));

export const StoryContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  backgroundColor: colors.whiteText,
  display: 'flex',
  flexDirection: 'column',
  padding: '48px 48.5px',
  gap: '17px',
  [theme.breakpoints.down('lg')]: {
    padding: '50px 40px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '40px 30px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 20px',
    gap: '15px',
  },
}));

export const StoryImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    height: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '400px',
  },
}));

export const StoryImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  [theme.breakpoints.down('md')]: {
    objectPosition: 'center -100px',
  },
  [theme.breakpoints.down('sm')]: {
    objectPosition: 'center',
  },
}));

export const StoryTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: colors.text,
  ...theme.typography.heading,
  fontSize: '50px',
  fontWeight: 400,
  fontFamily: fontFamilies.gotham,
  letterSpacing: '0px',
  lineHeight: '58px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '42px',
    lineHeight: '50px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
    lineHeight: '44px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '30px',
    lineHeight: '38px',
  },
}));

export const StorySubtitle = styled(Typography)(({ theme }) => ({
  color: colors.text,
  letterSpacing: '0px',
  fontWeight: 500,
  fontSize: '25px',
  lineHeight: '37px',
  whiteSpace: 'pre-line',

  fontFamily: fontFamilies.quicksand,
  [theme.breakpoints.down('lg')]: {
    fontSize: '22px',
    lineHeight: '33px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
    lineHeight: '30px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
}));

export const StoryQuote = styled(Typography)(({ theme }) => ({
  letterSpacing: '-0.4px',
  fontWeight: 500,
  fontSize: '28px',

  color: colors.secondary,
  fontStyle: 'italic',
  whiteSpace: 'pre-line',
  lineHeight: '42px',
  textAlign: 'center',
  fontFamily: fontFamilies.gotham,
  [theme.breakpoints.down('lg')]: {
    fontSize: '28px',
    lineHeight: '38px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    lineHeight: '34px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
    lineHeight: '28px',
  },
}));

export const StoryQuoteSmall = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '22px',
  lineHeight: '32px',
  color: colors.text,
  fontFamily: fontFamilies.quicksand,
  margin: '0 0 35px',
  letterSpacing: '0.4px',
  whiteSpace: 'pre-line',
  [theme.breakpoints.down('lg')]: {
    fontSize: '20px',
    lineHeight: '30px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    lineHeight: '27px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
    lineHeight: '23px',
  },
}));

export const StoryQuoteSmallItalic = styled(StoryQuoteSmall)(({ theme }) => ({
  fontStyle: 'italic',
  color: colors.secondary,
  letterSpacing: '0.4px',
  fontFamily: fontFamilies.gotham,
  fontWeight: 400,
  fontSize: '22px',
  lineHeight: '32px',
}));

export const JourneyCardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: theme.spacing(18.75),
  alignItems: 'center',
  transform: 'scale(0.4)',
  overflowX: 'auto',
  padding: theme.spacing(0, 1, 1),
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch',
  justifyContent: 'flex-start',
  marginTop: '15px',
  [theme.breakpoints.up('sm')]: {
    gridAutoColumns: theme.spacing(20),
  },
  [theme.breakpoints.up('md')]: {
    gridAutoColumns: '202px',
    transform: 'scale(1)',
    overflowX: 'visible',
    padding: 0,
    justifyContent: 'center',
    margin: '0 auto',
  },
  '& > *': {
    minWidth: theme.spacing(18.75),
    [theme.breakpoints.up('sm')]: {
      minWidth: theme.spacing(20),
    },
    [theme.breakpoints.up('md')]: {
      minWidth: theme.spacing(21.25),
    },
  },
  '&::-webkit-scrollbar': {
    height: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.18)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
}));

export const JourneyButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  margin: '77px auto 0',
  height: '60px',
  [theme.breakpoints.down('md')]: {
    margin: '50px auto 0',
  },
  [theme.breakpoints.down('sm')]: {
    margin: '36px auto 0',
  },
}));

export const JourneyButton = styled(CTAButton)(({ theme }) => ({
  padding: '20px 50px',
  margin: 0,
  minWidth: '297px',
  [theme.breakpoints.down('md')]: {
    padding: '16px 40px',
    minWidth: '260px',
    fontSize: '18px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '14px 30px',
    minWidth: '220px',
    fontSize: '16px',
    width: '100%',
    maxWidth: '297px',
  },
}));
